<template>
  <div class="sdb-banner">
    <!-- Header -->
    <div class="sdb-header">
      <span class="sdb-icon">⚠</span>
      <div class="sdb-header-body">
        <span class="sdb-title">Diff may be out of date</span>
        <span class="sdb-subtitle">
          {{ totalStaleCount }} BOM{{ totalStaleCount !== 1 ? "s" : "" }}
          {{ totalStaleCount === 1 ? "has" : "have" }} changed since the diff
          was computed<template v-if="diffComputedAt">
            on {{ formatDate(diffComputedAt) }}</template
          >. Proceeding with this diff may apply outdated changes.
        </span>
      </div>
    </div>

    <!-- Deleted BOMs -->
    <div v-if="deletedBoms.length" class="sdb-section">
      <div class="sdb-section-title sdb-section-title-red">
        {{ deletedBoms.length }} BOM{{ deletedBoms.length !== 1 ? "s" : "" }}
        deleted
      </div>
      <div class="sdb-deleted-list">
        <div
          v-for="bom in deletedBoms"
          :key="resolveCode(bom)"
          class="sdb-deleted-item"
        >
          <span class="sdb-bom-code">{{ resolveCode(bom) }}</span>
          <template v-if="resolveName(bom)">
            <span class="sdb-sep">—</span>
            <span class="sdb-bom-name">{{ resolveName(bom) }}</span>
          </template>
          <span class="sdb-badge sdb-badge-deleted">Deleted</span>
        </div>
      </div>
      <div class="sdb-deleted-note">
        These BOMs will be automatically excluded from the Apply step.
      </div>
    </div>

    <!-- Stale BOMs -->
    <div v-if="staleBoms.length" class="sdb-section">
      <div class="sdb-section-title">
        {{ staleBoms.length }} BOM{{ staleBoms.length !== 1 ? "s" : "" }}
        modified since diff was computed
      </div>
      <div
        v-for="bom in staleBoms"
        :key="bom.BomCode"
        :class="[
          'sdb-stale-item',
          { 'sdb-stale-item-open': expandedBoms[bom.BomCode] },
        ]"
      >
        <!-- Collapsible header -->
        <div class="sdb-stale-hdr" @click="toggleBom(bom.BomCode)">
          <span class="sdb-chevron">{{
            expandedBoms[bom.BomCode] ? "▼" : "▶"
          }}</span>
          <span class="sdb-bom-code">{{ bom.BomCode }}</span>
          <template v-if="bom.ProductName">
            <span class="sdb-sep">—</span>
            <span class="sdb-bom-name">{{ bom.ProductName }}</span>
          </template>
          <span v-if="bom.DateChanged" class="sdb-changed-at">
            changed {{ formatDate(bom.DateChanged) }}
          </span>
          <span class="sdb-lines-count">
            {{ (bom.ChangedLines || []).length }} line{{
              (bom.ChangedLines || []).length !== 1 ? "s" : ""
            }}
            changed
          </span>
        </div>

        <!-- Changed lines table -->
        <div v-if="expandedBoms[bom.BomCode]" class="sdb-lines-table">
          <div class="sdb-lines-head">
            <div class="sdb-lth sdb-lcol-proc">Process</div>
            <div class="sdb-lth sdb-lcol-code">Code</div>
            <div class="sdb-lth sdb-lcol-val">Stored value</div>
            <div class="sdb-lth sdb-lcol-val">Current value</div>
            <div class="sdb-lth sdb-lcol-chg">Change</div>
          </div>
          <div
            v-for="(line, i) in bom.ChangedLines"
            :key="i"
            class="sdb-ltr"
          >
            <div class="sdb-ltc sdb-lcol-proc">
              {{ line.ProcessCode || "—" }}
            </div>
            <div class="sdb-ltc sdb-lcol-code sdb-code-bold">
              {{ line.Code }}
            </div>
            <div class="sdb-ltc sdb-lcol-val sdb-val-old">
              {{ formatVal(line.StoredValue) }}
            </div>
            <div class="sdb-ltc sdb-lcol-val sdb-val-new">
              {{ formatVal(line.CurrentValue) }}
            </div>
            <div class="sdb-ltc sdb-lcol-chg">
              <span
                :class="[
                  'sdb-pill',
                  'sdb-pill-' + changeColor(line.ChangeType),
                ]"
                >{{ changeLabel(line.ChangeType) }}</span
              >
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Action section -->
    <div v-if="!isReadOnly" class="sdb-actions">
      <div class="sdb-action-col">
        <span class="sdb-action-label">Option 1 — recompute</span>
        <p class="sdb-action-desc">
          Re-runs the diff against the current BOM data. Recommended if you
          want the most up-to-date preview before applying.
        </p>
        <DxButton
          text="Recompute diff"
          type="default"
          styling-mode="contained"
          class="sdb-recompute-btn"
          @click="$emit('recompute')"
        />
      </div>

      <div class="sdb-action-divider">or</div>

      <div class="sdb-action-col">
        <span class="sdb-action-label">Option 2 — proceed with stored diff</span>
        <p class="sdb-action-desc">
          Enter a reason to acknowledge the discrepancy and proceed without
          recomputing. The reason will be recorded in the activity log.
        </p>
        <textarea
          v-model.trim="acknowledgementReason"
          class="sdb-reason-input"
          placeholder="Explain why proceeding with the stored diff is acceptable..."
          rows="2"
          maxlength="500"
        />
        <span class="sdb-proceed-wrap">
          <DxButton
            text="Proceed with stored diff"
            type="normal"
            styling-mode="outlined"
            :disabled="!acknowledgementReason"
            class="sdb-proceed-btn"
            @click="onProceed"
          />
        </span>
      </div>
    </div>
  </div>
</template>

<script>
import DxButton from "devextreme-vue/button";
import { formatNumber } from "devextreme/localization";

export default {
  name: "GcBomBulkUpdateStaleDiffBanner",
  components: { DxButton },
  props: {
    staleBoms: { type: Array, required: true },
    deletedBoms: { type: Array, required: true },
    diffComputedAt: { type: String, required: true },
    isReadOnly: { type: Boolean, default: false },
  },
  data() {
    return {
      acknowledgementReason: "",
      expandedBoms: {},
    };
  },
  computed: {
    totalStaleCount() {
      return this.staleBoms.length + this.deletedBoms.length;
    },
  },
  created() {
    // Default all stale BOMs to expanded so changed lines are visible immediately
    this.staleBoms.forEach((bom) => {
      this.$set(this.expandedBoms, bom.BomCode, true);
    });
  },
  methods: {
    toggleBom(bomCode) {
      this.$set(this.expandedBoms, bomCode, !this.expandedBoms[bomCode]);
    },
    resolveCode(bom) {
      return typeof bom === "string" ? bom : bom.BomCode;
    },
    resolveName(bom) {
      return typeof bom === "object" ? bom.ProductName || null : null;
    },
    onProceed() {
      if (!this.acknowledgementReason) return;
      this.$emit("acknowledge", this.acknowledgementReason);
    },
    changeColor(changeType) {
      if (["QtyChanged", "RateChanged"].includes(changeType)) return "amber";
      if (changeType === "Added") return "green";
      if (["Replaced", "Removed"].includes(changeType)) return "red";
      return "grey";
    },
    changeLabel(changeType) {
      const labels = {
        QtyChanged: "Qty changed",
        RateChanged: "Rate changed",
        Added: "Added",
        Replaced: "Replaced",
        Removed: "Removed",
        NoChange: "No change",
        NotApplicable: "N/A",
      };
      return labels[changeType] || changeType;
    },
    formatVal(value) {
      if (value == null) return "—";
      if (typeof value === "string") return value;
      return formatNumber(value, "##,###,###,###,##0.####");
    },
    formatDate(dateStr) {
      if (!dateStr) return "";
      try {
        return new Date(dateStr).toLocaleDateString("en-AU", {
          year: "numeric",
          month: "short",
          day: "numeric",
          hour: "2-digit",
          minute: "2-digit",
        });
      } catch {
        return dateStr;
      }
    },
  },
};
</script>

<style lang="scss">
.sdb-banner {
  border: 1px solid #f79009;
  border-radius: 8px;
  background: #fffcf0;
  margin-bottom: 10px;
  overflow: hidden;
}

// ── Header ────────────────────────────────────────────────────────────────────
.sdb-header {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 14px 16px;
  background: #fff3c7;
  border-bottom: 1px solid #f79009;
}

.sdb-icon {
  font-size: 18px;
  flex-shrink: 0;
  margin-top: 1px;
  color: #b54708;
}

.sdb-header-body {
  display: flex;
  flex-direction: column;
  gap: 3px;
}

.sdb-title {
  font-size: 14px;
  font-weight: 700;
  color: #7a2e0e;
}

.sdb-subtitle {
  font-size: 13px;
  color: #92400e;
  line-height: 1.4;
}

// ── Sections ──────────────────────────────────────────────────────────────────
.sdb-section {
  padding: 12px 16px;
  border-bottom: 1px solid #fde8a0;
}

.sdb-section-title {
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #92400e;
  margin-bottom: 10px;

  &.sdb-section-title-red {
    color: #9b1c1c;
  }
}

// ── Deleted BOMs ──────────────────────────────────────────────────────────────
.sdb-deleted-list {
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-bottom: 8px;
}

.sdb-deleted-item {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
}

.sdb-deleted-note {
  font-size: 12px;
  color: #6c757d;
  font-style: italic;
}

.sdb-badge {
  display: inline-block;
  padding: 1px 8px;
  border-radius: 9999px;
  font-size: 11px;
  font-weight: 600;
}

.sdb-badge-deleted {
  background: #fee4e2;
  color: #9b1c1c;
  border: 1px solid #f97066;
}

// ── Stale BOM entries ─────────────────────────────────────────────────────────
.sdb-stale-item {
  border: 1px solid #fde8a0;
  border-radius: 6px;
  overflow: hidden;
  margin-bottom: 6px;

  &:last-child {
    margin-bottom: 0;
  }
}

.sdb-stale-hdr {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  background: #fffbeb;
  cursor: pointer;
  user-select: none;
  flex-wrap: wrap;

  &:hover {
    background: #fef3c7;
  }
}

.sdb-chevron {
  font-size: 9px;
  color: #92400e;
  flex-shrink: 0;
}

.sdb-bom-code {
  font-size: 13px;
  font-weight: 600;
  color: #101828;
}

.sdb-sep {
  color: #adb5bd;
  font-size: 13px;
}

.sdb-bom-name {
  font-size: 13px;
  color: #344054;
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.sdb-changed-at {
  font-size: 11px;
  color: #b54708;
  white-space: nowrap;
  margin-left: auto;
}

.sdb-lines-count {
  font-size: 11px;
  font-weight: 600;
  color: #92400e;
  background: #fde8a0;
  padding: 1px 8px;
  border-radius: 9999px;
  white-space: nowrap;
  flex-shrink: 0;
}

// ── Changed lines table ───────────────────────────────────────────────────────
.sdb-lines-table {
  font-size: 12px;
  border-top: 1px solid #fde8a0;
}

.sdb-lines-head {
  display: flex;
  background: #fef9e7;
  border-bottom: 1px solid #fde8a0;
}

.sdb-lth {
  padding: 5px 8px;
  font-size: 11px;
  font-weight: 600;
  color: #92400e;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  white-space: nowrap;
}

.sdb-ltr {
  display: flex;
  border-bottom: 1px solid #fef3c7;

  &:last-child {
    border-bottom: none;
  }
}

.sdb-ltc {
  padding: 5px 8px;
  color: #344054;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

// Column widths
.sdb-lcol-proc {
  width: 90px;
  flex-shrink: 0;
}

.sdb-lcol-code {
  width: 110px;
  flex-shrink: 0;
}

.sdb-lcol-val {
  width: 120px;
  flex-shrink: 0;
  text-align: right;
}

.sdb-lcol-chg {
  width: 120px;
  flex-shrink: 0;
}

.sdb-code-bold {
  font-weight: 600;
}

.sdb-val-old {
  color: #6c757d;
  text-decoration: line-through;
}

.sdb-val-new {
  font-weight: 600;
  color: #101828;
}

// ── Pills (reuse colour map from bdp-panel) ───────────────────────────────────
.sdb-pill {
  display: inline-block;
  padding: 1px 7px;
  border-radius: 9999px;
  font-size: 11px;
  font-weight: 500;
  white-space: nowrap;

  &.sdb-pill-amber {
    background: #fef0c7;
    color: #92400e;
    border: 1px solid #f79009;
  }

  &.sdb-pill-green {
    background: #d1fadf;
    color: #065f46;
    border: 1px solid #17b26a;
  }

  &.sdb-pill-red {
    background: #fee4e2;
    color: #9b1c1c;
    border: 1px solid #f97066;
  }

  &.sdb-pill-grey {
    background: #f8f9fa;
    color: #6c757d;
    border: 1px solid #dee2e6;
  }
}

// ── Actions ───────────────────────────────────────────────────────────────────
.sdb-actions {
  display: flex;
  align-items: flex-start;
  gap: 0;
  padding: 16px;
}

.sdb-action-col {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.sdb-action-label {
  font-size: 12px;
  font-weight: 700;
  color: #344054;
  text-transform: uppercase;
  letter-spacing: 0.4px;
}

.sdb-action-desc {
  font-size: 12px;
  color: #6c757d;
  margin: 0;
  line-height: 1.4;
}

.sdb-action-divider {
  flex-shrink: 0;
  width: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 600;
  color: #adb5bd;
  padding-top: 48px;
}

.sdb-reason-input {
  width: 100%;
  padding: 6px 10px;
  border: 1px solid #ced4da;
  border-radius: 4px;
  font-size: 13px;
  font-family: inherit;
  resize: vertical;
  min-height: 52px;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: #03a9f4;
    box-shadow: 0 0 0 2px rgba(3, 169, 244, 0.15);
  }

  &::placeholder {
    color: #adb5bd;
  }
}

.sdb-recompute-btn {
  align-self: flex-start;
}

.sdb-proceed-wrap {
  align-self: flex-start;
}
</style>
